import { DrumSynth, DRUM_NOTES } from '../../instruments/drum/DrumSynth';
import { SynthVoice } from '../../instruments/synth/SynthVoice';
import { SamplerSynth } from '../../instruments/sampler/SamplerSynth';
import { sampleBank } from '../../core/samples/SampleBank';
import { EffectChain } from '../../effects/EffectChain';
import { preloadWorklets } from '../../effects/worklets';
import { Channel, Pattern, Project, stepsPerBarFor } from '../project/types';
import { Transport } from '../transport/Transport';

interface ChannelNodes {
  input: GainNode; // voices & drum triggers connect here
  chain: EffectChain; // insert effects
  gain: GainNode; // volume
  panner: StereoPannerNode;
  analyser: AnalyserNode;
  out: GainNode; // to masterInput
  sends: Map<string, GainNode>; // busId → send gain
}

interface BusNodes {
  input: GainNode;
  chain: EffectChain;
  gain: GainNode;
  panner: StereoPannerNode;
  analyser: AnalyserNode;
  out: GainNode;
}

/** Live PCM capture from the master bus (or a mic) via ScriptProcessor. */
interface CaptureSession {
  source: 'mic' | 'internal';
  proc: ScriptProcessorNode;
  zero: GainNode;
  chunks: Float32Array<ArrayBuffer>[]; // alternating ch0, ch1 blocks
  micStream: MediaStream | null;
  micSource: MediaStreamAudioSourceNode | null;
}

const CAPTURE_BLOCK = 4096;

/**
 * AudioEngine — owns the AudioContext, the master chain, per-channel and
 * per-bus subgraphs (each with an insert EffectChain), all live voices, and
 * the transport. The engine mirrors the project document: the store calls
 * setProject() after every mutation and the engine patches only what changed.
 *
 * Per-channel graph:
 *   voices → input → [insert FX] → gain → panner → analyser → out → master
 *                    └→ sendGain[bus] → bus.input
 * Per-bus graph:
 *   bus.input → [bus FX] → gain → panner → analyser → out → master
 */
export class AudioEngine {
  ctx: AudioContext | null = null;
  transport: Transport | null = null;

  private masterInput: GainNode | null = null;
  private masterGain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  masterAnalyser: AnalyserNode | null = null;

  private channels = new Map<string, ChannelNodes>();
  private buses = new Map<string, BusNodes>();
  private liveVoices = new Map<string, Map<number, SynthVoice>>();
  private scheduledVoices = new Set<SynthVoice>();
  private drumSynth: DrumSynth | null = null;
  private samplerSynth: SamplerSynth | null = null;
  private capture: CaptureSession | null = null;
  /** Session view: channelId → active clip. Empty = no session playback. */
  private sessionActive = new Map<string, { patternId: string; startStep: number }>();

  private project: Project | null = null;

  get ready(): boolean {
    return this.ctx !== null;
  }

  /** Must be called from a user gesture (autoplay policy). Idempotent. */
  async ensureContext(): Promise<AudioContext> {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      return this.ctx;
    }
    const ctx = new AudioContext({ latencyHint: 'interactive' });
    this.ctx = ctx;

    this.masterInput = ctx.createGain();
    this.masterGain = ctx.createGain();
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -6;
    this.limiter.knee.value = 4;
    this.limiter.ratio.value = 12;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.25;
    this.masterAnalyser = ctx.createAnalyser();
    this.masterAnalyser.fftSize = 1024;

    this.masterInput.connect(this.masterGain);
    this.masterGain.connect(this.limiter);
    this.limiter.connect(this.masterAnalyser);
    this.masterAnalyser.connect(ctx.destination);

    await preloadWorklets(ctx);

    this.drumSynth = new DrumSynth(ctx);
    this.samplerSynth = new SamplerSynth(ctx);
    sampleBank.ensureBuiltins(ctx);
    this.transport = new Transport(() => ctx.currentTime);
    this.transport.onStep = (step, time) => this.scheduleStep(step, time);

    this.applyProject();
    return ctx;
  }

  /** The store calls this after every project mutation. */
  setProject(project: Project): void {
    this.project = project;
    if (!this.ctx) return;
    this.applyProject();
  }

  private applyProject(): void {
    const p = this.project;
    const ctx = this.ctx;
    if (!p || !ctx || !this.masterInput || !this.masterGain) return;

    this.syncBuses(p);
    this.syncChannels(p);

    this.masterGain.gain.setTargetAtTime(p.masterVolume, ctx.currentTime, 0.015);
    this.transport?.setBpm(p.bpm);
    this.transport?.setLoop(p.loop);
    this.transport?.setMeter(stepsPerBarFor(p.timeSig.beats, p.timeSig.unit), p.timeSig.beats);
  }

  private syncBuses(p: Project): void {
    const ctx = this.ctx!;
    for (const [id, nodes] of this.buses) {
      if (!p.buses.some((b) => b.id === id)) {
        nodes.out.disconnect();
        nodes.chain.dispose();
        this.buses.delete(id);
      }
    }
    for (const bus of p.buses) {
      let nodes = this.buses.get(bus.id);
      if (!nodes) {
        nodes = this.buildBus();
        this.buses.set(bus.id, nodes);
      }
      nodes.chain.setSlots(bus.effects);
      nodes.gain.gain.setTargetAtTime(bus.volume, ctx.currentTime, 0.015);
      nodes.panner.pan.setTargetAtTime(bus.pan, ctx.currentTime, 0.015);
    }
  }

  private syncChannels(p: Project): void {
    const ctx = this.ctx!;
    const anySolo = p.channels.some((c) => c.solo);

    for (const [id, nodes] of this.channels) {
      if (!p.channels.some((c) => c.id === id)) {
        this.releaseVoicesFor(id);
        nodes.out.disconnect();
        for (const send of nodes.sends.values()) send.disconnect();
        nodes.chain.dispose();
        this.channels.delete(id);
        this.liveVoices.delete(id);
      }
    }

    for (const ch of p.channels) {
      let nodes = this.channels.get(ch.id);
      if (!nodes) {
        nodes = this.buildChannel();
        this.channels.set(ch.id, nodes);
        this.liveVoices.set(ch.id, new Map());
      }
      nodes.chain.setSlots(ch.effects);
      const effective = ch.muted || (anySolo && !ch.solo) ? 0 : ch.volume;
      nodes.gain.gain.setTargetAtTime(effective, ctx.currentTime, 0.015);
      nodes.panner.pan.setTargetAtTime(ch.pan, ctx.currentTime, 0.015);
      this.syncSends(ch, nodes);
    }
  }

  private syncSends(ch: Channel, nodes: ChannelNodes): void {
    const ctx = this.ctx!;
    // Drop sends to buses that no longer exist.
    for (const [busId, send] of nodes.sends) {
      if (!this.buses.has(busId)) {
        send.disconnect();
        nodes.sends.delete(busId);
      }
    }
    for (const bus of this.project!.buses) {
      const level = ch.sends[bus.id] ?? 0;
      let send = nodes.sends.get(bus.id);
      if (!send) {
        send = ctx.createGain();
        send.gain.value = 0;
        nodes.chain.output.connect(send);
        send.connect(this.buses.get(bus.id)!.input);
        nodes.sends.set(bus.id, send);
      }
      send.gain.setTargetAtTime(level, ctx.currentTime, 0.015);
    }
  }

  private buildChannel(): ChannelNodes {
    const ctx = this.ctx!;
    const input = ctx.createGain();
    const chain = new EffectChain(ctx);
    const gain = ctx.createGain();
    const panner = ctx.createStereoPanner();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    const out = ctx.createGain();
    input.connect(chain.input);
    chain.output.connect(gain);
    gain.connect(panner);
    panner.connect(analyser);
    analyser.connect(out);
    out.connect(this.masterInput!);
    return { input, chain, gain, panner, analyser, out, sends: new Map() };
  }

  private buildBus(): BusNodes {
    const ctx = this.ctx!;
    const input = ctx.createGain();
    const chain = new EffectChain(ctx);
    const gain = ctx.createGain();
    const panner = ctx.createStereoPanner();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    const out = ctx.createGain();
    input.connect(chain.input);
    chain.output.connect(gain);
    gain.connect(panner);
    panner.connect(analyser);
    analyser.connect(out);
    out.connect(this.masterInput!);
    return { input, chain, gain, panner, analyser, out };
  }

  channelAnalyser(channelId: string): AnalyserNode | null {
    return this.channels.get(channelId)?.analyser ?? null;
  }

  /** Play a bank sample straight to the master (browser preview). */
  previewSample(sampleId: string): void {
    const ctx = this.ctx;
    if (!ctx || !this.masterInput) return;
    const s = sampleBank.get(sampleId);
    if (!s) return;
    const src = ctx.createBufferSource();
    src.buffer = s.buffer;
    const g = ctx.createGain();
    g.gain.value = 0.8;
    src.connect(g);
    g.connect(this.masterInput);
    src.start();
  }

  // ── Capture (recording + bounce) ──────────────────────────────────────────

  private beginCapture(source: 'mic' | 'internal'): CaptureSession {
    const ctx = this.ctx!;
    const proc = ctx.createScriptProcessor(CAPTURE_BLOCK, 2, 2);
    const zero = ctx.createGain();
    zero.gain.value = 0;
    const chunks: Float32Array<ArrayBuffer>[] = [];
    proc.onaudioprocess = (e) => {
      for (let ch = 0; ch < 2; ch++) {
        chunks.push(new Float32Array(e.inputBuffer.getChannelData(ch)));
      }
    };
    let micStream: MediaStream | null = null;
    let micSource: MediaStreamAudioSourceNode | null = null;
    if (source === 'internal') {
      // Tap the final mix (post-limiter): what you hear is what you get.
      this.masterAnalyser!.connect(proc);
    }
    proc.connect(zero);
    // The capture branch must reach the destination or Chrome won't pull it.
    zero.connect(ctx.destination);
    return { source, proc, zero, chunks, micStream, micSource };
  }

  private endCapture(cap: CaptureSession): void {
    cap.proc.disconnect();
    cap.zero.disconnect();
    if (cap.source === 'internal' && this.masterAnalyser) {
      this.masterAnalyser.disconnect(cap.proc);
    }
    if (cap.micSource) cap.micSource.disconnect();
    if (cap.micStream) {
      for (const t of cap.micStream.getTracks()) t.stop();
    }
  }

  /** Begin recording. `mic` requests getUserMedia (user gesture required). */
  async startRecording(source: 'mic' | 'internal'): Promise<void> {
    const ctx = await this.ensureContext();
    if (this.capture) return;
    const cap = this.beginCapture(source);
    if (source === 'mic') {
      cap.micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      cap.micSource = ctx.createMediaStreamSource(cap.micStream);
      cap.micSource.connect(cap.proc);
    }
    this.capture = cap;
  }

  /** Stop recording and return the captured stereo buffer (null if empty). */
  async stopRecording(): Promise<AudioBuffer | null> {
    const ctx = this.ctx;
    const cap = this.capture;
    if (!ctx || !cap) return null;
    this.capture = null;
    this.endCapture(cap);
    const blocks = Math.floor(cap.chunks.length / 2);
    if (blocks === 0) return null;
    const len = blocks * CAPTURE_BLOCK;
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let b = 0; b < blocks; b++) {
      buf.copyToChannel(cap.chunks[b * 2], 0, b * CAPTURE_BLOCK);
      buf.copyToChannel(cap.chunks[b * 2 + 1], 1, b * CAPTURE_BLOCK);
    }
    return buf;
  }

  /**
   * Bounce the current loop (or one bar when looping is off) to stereo PCM,
   * including a short tail for releases/reverb. Restarts playback from the
   * loop start (no count-in clicks).
   */
  async bounce(): Promise<{ channels: Float32Array[]; sampleRate: number; seconds: number }> {
    const ctx = await this.ensureContext();
    const p = this.project;
    const t = this.transport;
    if (!p || !t || !this.masterAnalyser) throw new Error('Engine not ready');

    const range = t.loopRange;
    const start = range.enabled ? range.startStep : 0;
    const end = range.enabled ? range.endStep : start + t.currentStepsPerBar;
    const seconds = (end - start) / t.stepsPerSecond + 0.5; // tail

    const cap = this.beginCapture('internal');
    this.capture = cap;

    this.stop();
    const preRoll = 0.12;
    const seqStart = t.planStart(start, preRoll);
    t.start();

    const horizon = seqStart + seconds;
    while (ctx.currentTime < horizon) {
      await new Promise((r) => setTimeout(r, 20));
    }
    this.stop();
    this.endCapture(cap);
    this.capture = null;

    const blocks = Math.floor(cap.chunks.length / 2);
    const len = blocks * CAPTURE_BLOCK;
    const ch0 = new Float32Array(len);
    const ch1 = new Float32Array(len);
    for (let b = 0; b < blocks; b++) {
      ch0.set(cap.chunks[b * 2], b * CAPTURE_BLOCK);
      ch1.set(cap.chunks[b * 2 + 1], b * CAPTURE_BLOCK);
    }
    return { channels: [ch0, ch1], sampleRate: ctx.sampleRate, seconds };
  }

  busAnalyser(busId: string): AnalyserNode | null {
    return this.buses.get(busId)?.analyser ?? null;
  }

  // ── Transport controls ────────────────────────────────────────────────────

  async play(fromStep?: number): Promise<void> {
    const ctx = await this.ensureContext();
    const t = this.transport;
    if (!t || t.playing) return;

    const p = this.project;
    const bars = p?.countInBars ?? 0;
    const stepDur = 1 / t.stepsPerSecond;
    const spb = t.currentStepsPerBar;
    const countInSteps = bars * spb;
    const countInDur = countInSteps * stepDur;
    const seqStart = t.planStart(fromStep, 0.08 + countInDur);
    if (bars > 0) {
      // Metronome clicks fill the bars before the sequence starts.
      const countInStart = seqStart - countInDur;
      for (let s = 0; s < countInSteps; s++) {
        const time = countInStart + s * stepDur;
        if (time < ctx.currentTime) continue;
        this.metronomeClick(time, s % spb === 0);
      }
    }
    t.start();
  }

  private metronomeClick(time: number, accent: boolean): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = accent ? 1600 : 1000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.35, time);
    g.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
    osc.connect(g);
    g.connect(this.masterInput!);
    osc.start(time);
    osc.stop(time + 0.06);
  }

  stop(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const v of this.scheduledVoices) v.noteOff(now);
    this.scheduledVoices.clear();
    for (const byNote of this.liveVoices.values()) {
      for (const v of byNote.values()) v.noteOff(now);
      byNote.clear();
    }
    this.drumSynth?.stopAll();
    this.samplerSynth?.stopAll();
    this.transport?.stop();
  }

  seek(step: number): void {
    this.transport?.seek(step);
  }

  // ── Session view (clip launch grid) ───────────────────────────────────────

  /**
   * Launch a scene: every non-null cell starts its clip from step 0 of the
   * clip, on the next 16th-note boundary. Starts the transport if stopped.
   * Cells set to null are stopped.
   */
  async launchScene(cells: Record<string, string | null>): Promise<void> {
    await this.ensureContext();
    const t = this.transport;
    if (!t) return;
    const start = t.playing ? Math.ceil(t.positionStep) : 0;
    if (!t.playing) {
      t.planStart(0, 0.08);
      t.start();
    }
    for (const [chId, patternId] of Object.entries(cells)) {
      if (patternId && this.project?.patterns[patternId]) {
        this.sessionActive.set(chId, { patternId, startStep: start });
      } else {
        this.sessionActive.delete(chId);
      }
    }
  }

  /** Launch a single cell (solo clip). */
  async launchCell(channelId: string, patternId: string): Promise<void> {
    await this.ensureContext();
    const t = this.transport;
    if (!t || !this.project?.patterns[patternId]) return;
    const start = t.playing ? Math.ceil(t.positionStep) : 0;
    if (!t.playing) {
      t.planStart(0, 0.08);
      t.start();
    }
    this.sessionActive.set(channelId, { patternId, startStep: start });
  }

  /** Stop one channel's clip; halts the transport when nothing is left. */
  stopCell(channelId: string): void {
    this.sessionActive.delete(channelId);
    if (this.sessionActive.size === 0) this.stop();
  }

  /** Stop the whole session (transport + all clips). */
  stopSession(): void {
    this.sessionActive.clear();
    this.stop();
  }

  /** channelId → patternId of active clips (for UI highlighting). */
  get sessionSnapshot(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [chId, c] of this.sessionActive) out[chId] = c.patternId;
    return out;
  }

  get isPlaying(): boolean {
    return this.transport?.playing ?? false;
  }

  /** Fractional step position for UI (stable even before ctx exists). */
  get positionStep(): number {
    return this.transport?.positionStep ?? 0;
  }

  // ── Scheduling ────────────────────────────────────────────────────────────

  private scheduleStep(step: number, time: number): void {
    const p = this.project;
    const ctx = this.ctx;
    if (!p || !ctx) return;
    const stepDur = 1 / this.transport!.stepsPerSecond;

    // Session mode takes priority: launched clips loop from their own start
    // and everything else is silenced for the duration of the session.
    if (this.sessionActive.size > 0) {
      for (const [chId, clip] of this.sessionActive) {
        const ch = p.channels.find((c) => c.id === chId);
        const nodes = this.channels.get(chId);
        const pattern = p.patterns[clip.patternId];
        if (!ch || !nodes || !pattern || pattern.length === 0) continue;
        // Positive modulo keeps the clip's phase seamless across the
        // transport's loop wrap.
        const local = (((step - clip.startStep) % pattern.length) + pattern.length) % pattern.length;
        this.firePatternNotes(ch, pattern, local, time, stepDur, nodes.input, Infinity);
      }
      return;
    }

    const playlistMode = p.playlist.length > 0;

    for (const ch of p.channels) {
      const nodes = this.channels.get(ch.id);
      if (!nodes) continue;

      if (playlistMode) {
        for (const clip of p.playlist) {
          if (clip.channelId !== ch.id) continue;
          if (step < clip.startStep || step >= clip.startStep + clip.length) continue;
          const pattern = p.patterns[clip.patternId];
          if (!pattern || pattern.length === 0) continue;
          const local = (step - clip.startStep) % pattern.length;
          const roomInClip = clip.startStep + clip.length - step;
          this.firePatternNotes(ch, pattern, local, time, stepDur, nodes.input, roomInClip);
        }
      } else {
        const pattern = p.patterns[ch.patternId];
        if (!pattern || pattern.length === 0) continue;
        const local = step % pattern.length;
        this.firePatternNotes(ch, pattern, local, time, stepDur, nodes.input, Infinity);
      }
    }
  }

  private firePatternNotes(
    ch: Channel,
    pattern: Pattern,
    local: number,
    time: number,
    stepDur: number,
    dest: AudioNode,
    roomInClip: number,
  ): void {
    const ctx = this.ctx!;
    for (const n of pattern.notes) {
      if (n.start !== local) continue;
      // Clamp the hold to the pattern boundary and (in clips) the clip end.
      const effLen = Math.max(1, Math.min(n.length, pattern.length - n.start, roomInClip));
      const dur = effLen * stepDur;
      if (ch.kind === 'drum') {
        const sound = DRUM_NOTES[n.note];
        if (sound) this.drumSynth!.trigger(sound, time, n.velocity, dest, time + dur);
      } else if (ch.kind === 'sampler') {
        const sample = sampleBank.get(ch.sampler.sampleId);
        if (!sample) continue;
        const rate = Math.pow(2, (n.note - sample.rootNote) / 12);
        // Non-looping notes stop at the natural end or the gate, whichever first.
        const naturalEnd = time + sample.buffer.duration / rate;
        this.samplerSynth!.trigger({
          buffer: sample.buffer,
          time,
          rate,
          loop: ch.sampler.loop,
          gain: n.velocity / 100,
          dest,
          stopAt: Math.min(naturalEnd, time + dur),
        });
      } else {
        const voice = new SynthVoice(ctx, ch.synth, n.note, n.velocity, dest, time);
        voice.noteOff(time + dur);
        this.scheduledVoices.add(voice);
        voice.onEnd = () => this.scheduledVoices.delete(voice);
      }
    }
  }

  private releaseVoicesFor(channelId: string): void {
    if (!this.ctx) return;
    const byNote = this.liveVoices.get(channelId);
    if (!byNote) return;
    for (const voice of byNote.values()) voice.noteOff(this.ctx.currentTime);
    byNote.clear();
  }

  // ── Live playing (computer keyboard) ─────────────────────────────────────

  /** Trigger a note live on a channel (from the software keyboard). */
  noteOn(channelId: string, note: number, velocity = 100): void {
    const ctx = this.ctx;
    const p = this.project;
    if (!ctx || !p) return;
    const ch = p.channels.find((c) => c.id === channelId);
    if (!ch) return;
    const nodes = this.channels.get(channelId);
    if (!nodes) return;
    const now = ctx.currentTime;

    if (ch.kind === 'drum') {
      const sound = DRUM_NOTES[note];
      if (sound && this.drumSynth) this.drumSynth.trigger(sound, now, velocity, nodes.input);
      return;
    }
    if (ch.kind === 'sampler') {
      const sample = sampleBank.get(ch.sampler.sampleId);
      if (!sample || !this.samplerSynth) return;
      const rate = Math.pow(2, (note - sample.rootNote) / 12);
      const naturalEnd = now + sample.buffer.duration / rate;
      this.samplerSynth.trigger({
        buffer: sample.buffer,
        time: now,
        rate,
        loop: ch.sampler.loop,
        gain: velocity / 100,
        dest: nodes.input,
        // Live notes are gated: natural end for short samples, 2 s cap otherwise.
        stopAt: Math.min(naturalEnd, now + 2),
      });
      return;
    }
    const byNote = this.liveVoices.get(channelId)!;
    const existing = byNote.get(note);
    if (existing) {
      existing.noteOff(now);
      byNote.delete(note);
    }
    const voice = new SynthVoice(ctx, ch.synth, note, velocity, nodes.input, now);
    byNote.set(note, voice);
  }

  noteOff(channelId: string, note: number): void {
    if (!this.ctx) return;
    const byNote = this.liveVoices.get(channelId);
    if (!byNote) return;
    const voice = byNote.get(note);
    if (voice) {
      voice.noteOff(this.ctx.currentTime);
      byNote.delete(note);
    }
  }
}

/** Process-wide singleton — one audio engine per page. */
export const engine = new AudioEngine();